import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useAuthStore } from '@carouselgg/auth/store'
import { useMyOrgs } from '@carouselgg/auth/use-my-orgs'
import { getMyInvites, type MyInvite } from '../../../api/invites'
import { listMyMatches, type MyMatch } from '../../../api/matches'
import { listOrgTeamsWithOpenFA, type OrgTeamWithApps } from '../../../api/recruit'
import {
  getMyTeams,
  getStandings,
  listMyCompetitions,
  listOrgSeasons,
  type MyCompetition,
  type MyTeam,
  type OrgSeason,
  type StandingRow,
} from '../../../api/teams'
import { groupCompetitions } from '../competitions'
import {
  canManageTeam,
  isOrgAdmin,
  seasonHref,
  runsLeague as computeRunsLeague,
} from '../destinations'
import { personas } from '../greeting'
import { rankNeedsYou } from '../needsYou'
import { mergeUpcoming, type UpcomingSource } from '../upcoming'
import { leaguePlace } from '../home/homeModel'
import { widgetContext } from './buildContext'
import { DashboardDataContext, type DashboardData } from './context'
import { distinctReasons, keyDates, pendingCount, runningSeasons } from './selectors'

/**
 * Loads the board's data once and hands it to every widget.
 *
 * All the fetching lives here so that adding a widget costs no requests, and so
 * there is one readiness signal rather than one per panel. This is also the
 * single file a `GET /dashboard` endpoint would replace.
 */

/** Enough to fill the week row without turning it into the schedule page. */
const UPCOMING_LIMIT = 8

/** The home's to-do list groups by place, so it can hold more than the old three. */
const NEEDS_YOU_LIMIT = 8

/**
 * Seasons a league operator has live or is about to.
 *
 * `draft` is in the list because a draft season's registration date is exactly
 * the sort of thing the key-dates widget exists to surface; widgets that only
 * care about play in progress filter it back out themselves.
 */
const ACTIVE_STATUSES: OrgSeason['status'][] = ['draft', 'open', 'active', 'playoffs']

export function DashboardDataProvider({ children }: { children: ReactNode }) {
  const trust = useAuthStore((s) => s.trustScore)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const orgs = useMyOrgs(isAuthenticated)

  const [invites, setInvites] = useState<MyInvite[]>([])
  const [matches, setMatches] = useState<MyMatch[]>([])
  const [teams, setTeams] = useState<MyTeam[]>([])
  const [seasons, setSeasons] = useState<OrgSeason[]>([])
  const [competitions, setCompetitions] = useState<MyCompetition[]>([])
  const [standings, setStandings] = useState<Record<string, StandingRow[]>>({})
  const [openRosters, setOpenRosters] = useState<OrgTeamWithApps[]>([])
  const [waitingByOrg, setWaitingByOrg] = useState<Record<string, number>>({})
  const [loaded, setLoaded] = useState(false)

  // Fixed at mount: re-deriving on every render would let the greeting flip
  // mid-session at a boundary hour, which reads as a glitch.
  const [now] = useState(() => new Date())

  // One pass, in parallel. Every source is independent and any of them may be
  // unavailable, so a failure costs its own widget rather than the page.
  // Re-reads just the competitions, after a league contact changed.
  const reloadCompetitions = useCallback(() => {
    listMyCompetitions()
      .then(setCompetitions)
      .catch(() => {
        // Keep what is shown; the change itself was already saved.
      })
  }, [])

  useEffect(() => {
    let cancelled = false
    Promise.allSettled([
      getMyInvites(),
      listMyMatches({ upcoming: true, limit: 20 }),
      getMyTeams(),
      listMyCompetitions(),
    ]).then(([inviteRes, matchRes, teamRes, compRes]) => {
      if (cancelled) return
      if (inviteRes.status === 'fulfilled') setInvites(inviteRes.value.invites)
      if (matchRes.status === 'fulfilled') setMatches(matchRes.value)
      if (teamRes.status === 'fulfilled') setTeams(teamRes.value.teams)
      if (compRes.status === 'fulfilled') setCompetitions(compRes.value)
      setLoaded(true)
    })
    return () => {
      cancelled = true
    }
  }, [])

  // Seasons are per-org and only leagues have them, so this waits on the org
  // list rather than joining the fan-out above.
  // Keyed on a joined string rather than the array: useMyOrgs rebuilds its
  // array on every refetch, so depending on the array itself would refetch
  // every season whenever anything asked the org list to refresh, even when the
  // leagues in it had not changed.
  const leagueKey = useMemo(
    () =>
      orgs
        .filter((o) => o.type === 'league')
        .map((o) => o.id)
        .join(','),
    [orgs],
  )
  // Which league set the seasons below were fetched for. Readiness compares it
  // to the current key, so a stale answer for a previous org list never reads
  // as loaded.
  const [seasonsFor, setSeasonsFor] = useState<string | null>(null)
  useEffect(() => {
    const ids = leagueKey ? leagueKey.split(',') : []
    if (ids.length === 0) {
      setSeasons([])
      setSeasonsFor(leagueKey)
      return
    }
    let cancelled = false
    Promise.allSettled(ids.map((id) => listOrgSeasons(id))).then((results) => {
      if (cancelled) return
      const all = results.flatMap((r) => (r.status === 'fulfilled' ? r.value.seasons : []))
      setSeasons(all.filter((s) => ACTIVE_STATUSES.includes(s.status)))
      setSeasonsFor(leagueKey)
    })
    return () => {
      cancelled = true
    }
  }, [leagueKey])

  // Teams with people waiting on them, for the clubs and universities the
  // viewer administers. Leagues are excluded: they do not field teams.
  // Keyed the same way and for the same reason as the league set above.
  const adminOrgKey = useMemo(
    () =>
      orgs
        .filter((o) => o.type !== 'league' && isOrgAdmin(o.role))
        .map((o) => o.id)
        .join(','),
    [orgs],
  )
  useEffect(() => {
    const ids = adminOrgKey ? adminOrgKey.split(',') : []
    if (ids.length === 0) {
      setOpenRosters([])
      setWaitingByOrg({})
      return
    }
    let cancelled = false
    Promise.allSettled(ids.map((id) => listOrgTeamsWithOpenFA(id))).then((results) => {
      if (cancelled) return
      setOpenRosters(
        results.flatMap((r) => (r.status === 'fulfilled' ? r.value.teams : [])),
      )
      // Kept per club too, so each club's home panel can say who is waiting.
      const byOrg: Record<string, number> = {}
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') byOrg[ids[i]] = pendingCount(r.value.teams)
      })
      setWaitingByOrg(byOrg)
    })
    return () => {
      cancelled = true
    }
  }, [adminOrgKey])

  // Everything the widgets render has arrived. The org list comes from its own
  // hook with no loaded flag, so seasons being fetched for the CURRENT league
  // set is the latest signal available that it has resolved.
  const ready = loaded && seasonsFor === leagueKey

  // One row per competition; the endpoint returns one per (competition, team).
  const competitionGroups = useMemo(() => groupCompetitions(competitions), [competitions])

  // Standings for the seasons the player board shows. Keyed on the season ids
  // so a changed competition list refetches, and bounded to what renders —
  // a club owner can be in dozens and the home shows four.
  const standingsKey = competitionGroups
    .slice(0, 4)
    .map((g) => `${g.competition.organization_id}:${g.competition.season_id}`)
    .join(',')
  useEffect(() => {
    const pairs = standingsKey ? standingsKey.split(',') : []
    if (pairs.length === 0) return
    let cancelled = false
    Promise.allSettled(
      pairs.map((pair) => {
        const [orgID, seasonID] = pair.split(':')
        return getStandings(orgID, seasonID).then((r) => [seasonID, r.standings] as const)
      }),
    ).then((results) => {
      if (cancelled) return
      const next: Record<string, StandingRow[]> = {}
      for (const r of results) {
        if (r.status === 'fulfilled') next[r.value[0]] = r.value[1]
      }
      setStandings(next)
    })
    return () => {
      cancelled = true
    }
  }, [standingsKey])

  const needsYou = useMemo(
    () =>
      rankNeedsYou({
        trustScore: trust?.score ?? null,
        invites,
        matches,
        teams,
        seasons,
        seasonHref: (season) => seasonHref(season, orgs),
        seasonPlace: (season) => leaguePlace(orgs.find((o) => o.id === season.organization_id)),
        canManageTeam: (team) => canManageTeam(team, orgs),
      }, { limit: NEEDS_YOU_LIMIT }),
    [trust?.score, invites, matches, teams, seasons, orgs],
  )

  // Everything /matches/mine returns is a fixture the user is ROSTERED for, so
  // it is all "playing". Org-sourced fixtures would enter as additional sources
  // here with a weaker reason.
  const upcoming = useMemo(() => {
    const sources: UpcomingSource[] = [{ reason: 'playing', matches }]
    return mergeUpcoming(sources, { limit: UPCOMING_LIMIT })
  }, [matches])

  // Who the viewer is, derived once. Both the board (deciding which widgets to
  // place) and the widgets themselves (deciding what to say) read this, so
  // deriving it twice would be two chances to disagree.
  const ctx = useMemo(
    () =>
      widgetContext({
        personas: personas({ onRoster: teams.length > 0, orgs }),
        runsLeague: computeRunsLeague(orgs),
        hasOrgs: orgs.length > 0,
        hasTrust: trust !== null && trust !== undefined,
        counts: {
          needsYou: needsYou.length,
          upcoming: upcoming.length,
          upcomingReasons: distinctReasons(upcoming),
          seasons: runningSeasons(seasons).length,
          teams: teams.length,
          competitions: competitionGroups.length,
          contactsToManage: competitions.filter((c) => c.can_manage_contact).length,
          keyDates: keyDates(seasons, now).length,
          joinRequests: pendingCount(openRosters),
        },
      }),
    [
      teams,
      orgs,
      trust,
      needsYou.length,
      upcoming,
      seasons,
      competitionGroups.length,
      openRosters,
      now,
    ],
  )

  // Memoized over every field: without this, every widget on the board
  // re-renders whenever anything above it does.
  const value = useMemo<DashboardData>(
    () => ({
      invites,
      matches,
      teams,
      seasons,
      standings,
      orgs,
      trust,
      openRosters,
      waitingByOrg,
      needsYou,
      upcoming,
      competitionGroups,
      reloadCompetitions,
      ctx,
      now,
      ready,
    }),
    [
      invites,
      matches,
      teams,
      seasons,
      standings,
      orgs,
      trust,
      openRosters,
      waitingByOrg,
      needsYou,
      upcoming,
      competitionGroups,
      reloadCompetitions,
      ctx,
      now,
      ready,
    ],
  )

  return <DashboardDataContext.Provider value={value}>{children}</DashboardDataContext.Provider>
}
